import React, { useState, useEffect } from 'react';
import './Intake.css';
import './Intake2.css';
import './MemberView.css';
import { Progress, ChoiceList, ShockTable, money } from './IntakeSteps';
import IntakeJourney from './IntakeJourney';
import { fmtErr } from './IntakeAdmin';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const PASS_KEY = 'ik_member_unlocked_v1';

const Masthead = ({ subtitle }) => (
  <div className="ik-masthead">
    <img src="/logo.png" alt="Pro Stock Money Bowl" className="ik-logo" />
    <div>
      <div className="ik-eyebrow">Grateful Trust · The Serene System</div>
      <h1>Your Placement Profile</h1>
      <div className="ik-quiet" style={{ marginTop: '0.2rem' }}>{subtitle}</div>
    </div>
  </div>
);

const Gate = ({ onMember }) => {
  const [passcode, setPasscode] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/intake/gate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ passcode }),
      });
      const body = await res.json();
      if (!res.ok) throw new Error(fmtErr(body.detail));
      sessionStorage.setItem(PASS_KEY, passcode);
      onMember(passcode);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mv-gate-page" data-testid="intake2-gate">
      <div className="mv-gate-card">
        <div className="mv-gate-brand">
          <img src="/logo.png" alt="Pro Stock Money Bowl" className="mv-gate-logo" />
          <div className="mv-gate-titles">
            <div className="mv-gate-eyebrow">GRATEFUL TRUST</div>
            <h1 className="mv-gate-title">Your Placement Profile</h1>
            <div className="mv-gate-subtitle">Private · Founding member intake</div>
          </div>
        </div>
        <form className="mv-gate-form" onSubmit={submit}>
          <label className="mv-gate-label" htmlFor="ik2-passcode">Enter your access code</label>
          <input
            id="ik2-passcode"
            type="password"
            className="mv-gate-input"
            value={passcode}
            onChange={(e) => setPasscode(e.target.value)}
            autoComplete="off"
            data-testid="intake2-passcode-input"
          />
          {error && <div className="mv-gate-error" data-testid="intake2-gate-error">{error}</div>}
          <button type="submit" className="mv-gate-button" disabled={busy}
            data-testid="intake2-gate-submit">
            {busy ? 'Checking…' : 'Unlock'}
          </button>
        </form>
      </div>
    </div>
  );
};

const Part = ({ n, name }) => (
  <div className="ik2-part">Part {n} of 3 · {name}</div>
);

/* Every control gets its question restated in one plain sentence immediately above it. */
const Ask = ({ q, children, sub }) => (
  <div className="ik2-ask">
    {q !== undefined && <span className="ik2-ask-num">Question {q}</span>}
    <p className="ik2-ask-text">{children}</p>
    {sub && <p className="ik2-ask-sub">{sub}</p>}
  </div>
);

const Honesty = () => (
  <div className="ik2-honesty" data-testid="intake2-honesty-banner">
    <div className="ik2-honesty-head">Be radically honest here</div>
    <p>
      Nothing is verified, nothing is shared, and no answer scores you better or worse as a
      person. The only thing a flattering answer buys you is a worse recommendation. No two
      investors are alike. You are unique, so be honest and the system can help you get the
      best result for you.
    </p>
  </div>
);

/* ---------------------------------------------------------------- screens */

const ScreenIntro = () => (
  <div className="ik-card" data-testid="intake2-intro">
    <h2>Let's begin your journey with the Serene System.</h2>
    <p>
      Before anything else, ten quick questions. There are no right answers and nothing is
      checked against anything. We are trying to help guide you on whether this system makes
      sense for you and, if so, what we would suggest in terms of what percentage you put
      into the Serene System and what percentage you invest yourself in other things. We call
      this Core and Explore, with the Core being what you put into the Serene System.
    </p>
    <ul className="ik2-agenda">
      <li>
        <strong>First, about you.</strong> Your history as an investor, what you have actually
        done, and what you value. No two investors are alike.
      </li>
      <li>
        <strong>Then, the hard numbers.</strong> We will show you the worst fall this system
        had, had you invested your own money, and ask what you would honestly do and how you
        would feel.
      </li>
      <li>
        <strong>Last, an agreement with yourself.</strong> Written while everything is calm,
        so you have something to read when it is not.
      </li>
    </ul>
    <p className="ik-quiet">
      About four minutes. If you have never traded before, it is shorter, because we will skip
      the questions that would not apply to you.
    </p>
  </div>
);

const ScreenExperience = ({ config, b1, setB1, n, total }) => (
  <div className="ik-card">
    <Part n={1} name="About you" />
    <div className="ik-step-label">Step {n} of {total} · Your experience</div>
    <Ask q={1} sub="We mean decisions you made yourself, not a 401k or an account someone else manages.">
      Have you ever traded or invested your own money actively?
    </Ask>
    <ChoiceList options={config.experience_options} value={b1} onChange={setB1}
      testPrefix="intake2-b1" />
    {b1 === 0 && (
      <div className="ik-reason" data-testid="intake2-skip-notice">
        Good, that is a straight answer. It also means the next four questions about returns
        would be noise for you, so we will skip them.
      </div>
    )}
  </div>
);

const ScreenTypical = ({ config, b3, setB3, n, total }) => (
  <div className="ik-card">
    <Part n={1} name="About you" />
    <div className="ik-step-label">Step {n} of {total} · Your results</div>
    <Honesty />
    <Ask q={2} sub="Best years are easy to remember. The average is the only number that predicts anything, and almost nobody has it written down.">
      Add up your good years and your bad years together. What do you typically make in a
      year, as a percentage?
    </Ask>
    <ChoiceList options={config.typical_year_options} value={b3} onChange={setB3}
      testPrefix="intake2-b3" />
  </div>
);

const ScreenRepeat = ({ config, b4, b5, b6, setB4, setB5, setB6, n, total }) => (
  <div className="ik-card">
    <Part n={1} name="About you" />
    <div className="ik-step-label">Step {n} of {total} · Repeatability</div>
    <Honesty />
    <Ask q={3} sub="Not your best year. The number you would bet on happening again.">
      What annual return are you 90% or more confident you could repeat, year after year?
    </Ask>
    <ChoiceList options={config.confident_options} value={b4} onChange={setB4}
      testPrefix="intake2-b4" />

    <Ask q={4} sub={config.down_market_note}>
      Have you actually traded through a down market, meaning a calendar year when the S&P 500
      itself finished negative?
    </Ask>
    <ChoiceList options={config.down_year_options} value={b5} onChange={setB5}
      testPrefix="intake2-b5" />

    <Ask q={5}>
      Now be realistic. Could you still make the return you picked in question 3, in a year
      like that, when the index itself is negative?
    </Ask>
    <ChoiceList options={config.down_confidence_options} value={b6} onChange={setB6}
      testPrefix="intake2-b6" />
  </div>
);

const ScreenValues = ({ config, a1, a2, setA1, setA2, n, total, qTime, qAttention }) => {
  const s = config.value_slider;
  return (
    <div className="ik-card">
      <Part n={1} name="About you" />
      <div className="ik-step-label">Step {n} of {total} · What you value</div>
      <Ask q={qTime}>
        Please rate how important your free time is, compared with spending that time growing
        your investments.
      </Ask>
      <div className="ik2-scale-key">
        <div><span>10</span> Your free time is far more valuable than time spent growing investments</div>
        <div><span>5</span> They matter about equally</div>
        <div><span>1</span> Growing your investments is far more valuable than your free time</div>
      </div>
      <div className="ik2-slider-wrap">
        <input
          type="range"
          className="ik2-slider"
          min={s.min}
          max={s.max}
          value={a1}
          onChange={(e) => setA1(Number(e.target.value))}
          data-testid="intake2-a1-slider"
        />
        <div className="ik2-slider-value" data-testid="intake2-a1-value">
          {a1} of 10
          <span className="ik2-slider-read">
            {a1 >= 8 ? ' · my time matters far more'
              : a1 >= 6 ? ' · my time matters more, but not at any cost'
                : a1 === 5 ? ' · they matter about equally'
                  : a1 >= 3 ? ' · growing my investments matters more'
                    : ' · growing my investments matters far more'}
          </span>
        </div>
      </div>

      <Ask q={qAttention} sub="Answer for the life you actually have, not the one you intend to have.">
        How much attention do you genuinely want to give this money?
      </Ask>
      <ChoiceList options={config.attention_options} value={a2} onChange={setA2}
        testPrefix="intake2-a2" />
    </div>
  );
};

const ScreenAmount = ({ config, band, setBand, n, total, q }) => (
  <div className="ik-card">
    <Part n={2} name="The hard numbers" />
    <div className="ik-step-label">Step {n} of {total} · Your amount</div>
    <p>
      Now we put real money against it. The next two questions only work in dollars, because a
      percentage is easy to shrug at and a dollar figure is not.
    </p>
    <Ask q={q} sub="This is not a commitment, it only helps us guide you accurately. It stays private and is never shared.">
      Roughly how much money are you considering putting into an autopilot growth system like
      this one? Pick the closest range.
    </Ask>
    <ChoiceList
      options={config.bands.map((b) => ({ key: b.key, label: b.label, note: b.note }))}
      value={band}
      onChange={setBand}
      testPrefix="intake2-band"
    />
  </div>
);

const ScreenJourney = ({ config, band, n, total }) => {
  const b = config.bands.find((x) => x.key === band);
  return (
    <div className="ik-card">
      <Part n={2} name="The hard numbers" />
      <div className="ik-step-label">Step {n} of {total} · The journey</div>
      <h2>Before we ask anything else, watch this first.</h2>
      <p>
        To help you understand what investing with a system actually feels like, imagine you
        had put <span className="ik-accent">{money(b.mid)}</span> into it ten years ago. Here
        is what would have happened to that money, month by month, from left to right. Follow
        the journey, and watch the fall when it comes. The next two questions are about that
        fall.
      </p>
      <IntakeJourney journey={config.journey} allocation={b.mid} />
    </div>
  );
};

const ScreenMagnitude = ({ config, band, q1, setQ1, n, total, q }) => {
  const b = config.bands.find((x) => x.key === band);
  return (
    <div className="ik-card">
      <Part n={2} name="The hard numbers" />
      <div className="ik-step-label">Step {n} of {total} · The magnitude</div>
      <h2>Now the two numbers, in your money.</h2>
      <p>
        The left is the deepest fall the system has actually had since 2009. The right is
        deeper, and to be conservative it is the one to plan around, because our record has
        never contained a dot-com-scale collapse. The long-horizon study points to a drop of{' '}
        <span className="ik-accent">up to {Math.abs(config.planning_dd_pct)}%</span> as the
        figure to size against.
      </p>
      <ShockTable shock={b.shock} />
      <p className="ik-quiet" style={{ marginTop: '0.7rem' }}>
        These are paper losses during a drawdown, not realised losses, and you just watched one
        recover. But you have to live through them to get there.
      </p>
      <Ask q={q} sub="Not a percentage on a page. That number, on your own statement.">
        Picture {money(b.shock.planning.value)} on your statement, down{' '}
        {money(b.shock.planning.loss)} from where it was. What would you actually do?
      </Ask>
      <ChoiceList options={config.q1_options} value={q1} onChange={setQ1}
        testPrefix="intake2-q1" />
    </div>
  );
};

const ScreenGrind = ({ config, q2, setQ2, n, total, q }) => {
  const ex = config.grind_examples || [];
  return (
    <div className="ik-card">
      <Part n={2} name="The hard numbers" />
      <div className="ik-step-label">Step {n} of {total} · The duration</div>
      <h2>Now the harder one. Nothing crashes. You just fall behind.</h2>
      <p>
        There is no crisis. Markets climb steadily for 18 months. The system spends part of
        that stretch sitting in gold and Treasuries, so you finish the period up, but a friend
        who simply bought the Nasdaq and did nothing is up far more than you. Nothing is
        broken. This is the trade-off working exactly as designed.
      </p>
      <p>
        It has happened twice:{' '}
        {ex.map((e, i) => (
          <span key={e.year}>
            {i > 0 ? ' and ' : ''}
            <span className="ik-accent">
              {e.year}, when the system trailed the Nasdaq by {e.shortfall_pp} points
            </span>
          </span>
        ))}
        . Members who left in those years left at the worst possible moment.
      </p>
      <Ask q={q}>
        Eighteen months in, you are up, but someone who never used the system is up far more
        than you. How do you feel?
      </Ask>
      <ChoiceList options={config.q2_options} value={q2} onChange={setQ2}
        testPrefix="intake2-q2" />
    </div>
  );
};

const ScreenAgreement = ({ config, band, form, setForm, n, total }) => {
  const b = config.bands.find((x) => x.key === band);
  return (
    <div className="ik-card">
      <Part n={3} name="The agreement" />
      <div className="ik-step-label">Step {n} of {total} · Your agreement</div>
      <h2>This one is not for us. It is for you.</h2>
      <p>
        Downturns are not a malfunction. They are the toll on the road, and every road charges
        it. What we can tell you is that this system has been built to make that stretch
        shallower and shorter than simply owning the index, and that every single drawdown in
        its record has ended.
      </p>
      <p>
        So this is not a promise to us, and it is not a contract. It is a note from the calm
        version of you right now to a potentially frightened version of you at some time in
        the future, and the frightened version is the one who will read this. It will say to
        yourself that you knew this could happen and you will be alright:{' '}
        <span className="ik-accent">this too shall pass.</span>
      </p>
      <div className="ik-commit">
        <p className="ik-commit-quote">
          “I have seen what a drop of up to {Math.abs(config.planning_dd_pct)}% looks like on
          my own money: {money(b.shock.planning.value)}, down{' '}
          {money(b.shock.planning.loss)}. I know a stretch like that is part of the journey
          and not a sign the system is broken. When it arrives and it does not feel good, I
          will read this back and remember that I decided this while I was thinking clearly.
          This too shall pass. I will not sell at the bottom.”
        </p>
        <label className="ik-check">
          <input type="checkbox" checked={form.signed}
            onChange={(e) => setForm({ ...form, signed: e.target.checked })}
            data-testid="intake2-commit-checkbox" />
          <span>I have read that, I mean it, and I am making this agreement with myself.</span>
        </label>
      </div>
      {form.signed && (
        <div className="ik-field">
          <label className="ik-label" htmlFor="ik2-sig">Sign by typing your name</label>
          <input id="ik2-sig" className="ik-input ik-signature" type="text"
            value={form.signature}
            onChange={(e) => setForm({ ...form, signature: e.target.value })}
            placeholder="Jane Doe" data-testid="intake2-signature-input" />
        </div>
      )}
      {!form.signed && (
        <p className="ik-quiet">
          You can continue without signing. We will lower the ceiling one step, which is the
          right thing to do, and you can retake this whenever you like.
        </p>
      )}
      <div className="ik-field">
        <label className="ik-label" htmlFor="ik2-name">Your full name</label>
        <input id="ik2-name" className="ik-input" type="text" value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          placeholder="Jane Doe" data-testid="intake2-name-input" />
      </div>
      <div className="ik-field">
        <label className="ik-label" htmlFor="ik2-email">Email</label>
        <input id="ik2-email" className="ik-input" type="email" value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
          placeholder="jane@example.com" data-testid="intake2-email-input" />
      </div>
    </div>
  );
};

const Result = ({ config, band, placement, chosen, setChosen, onSave, saved, saving, onRestart }) => {
  const b = config.bands.find((x) => x.key === band);
  const mid = b.mid;
  const core$ = Math.round(mid * (chosen / 100));
  const rest$ = mid - core$;
  const rest = 100 - chosen;
  return (
    <>
      <div className="ik-card" data-testid="intake2-not-a-grade">
        <div className="ik-step-label">Read this first</div>
        <h2>This is a fit, not a grade.</h2>
        <p>{placement.not_a_grade}</p>
      </div>

      <div className="ik-card" data-testid="intake2-result">
        <div className="ik-step-label">Your ceiling</div>
        <div className="ik2-ceiling">
          <span className="ik2-ceiling-num" data-testid="intake2-ceiling">
            {placement.ceiling_pct}%
          </span>
          <span className="ik2-ceiling-key">
            the most we would suggest educationally for a system like this one
          </span>
        </div>
        <p>{placement.ceiling_sentence}</p>

        <div className="ik2-suggest" data-testid="intake2-suggested">
          Based on what you told us you value and what you have actually done, the share we
          would suggest is <strong>{placement.suggested_core_pct}%</strong>. Move the slider
          wherever it belongs for you.
        </div>

        <div className="ik2-slider-wrap">
          <input
            type="range"
            className="ik2-slider ik2-slider-core"
            min={0}
            max={placement.ceiling_pct}
            step={5}
            value={chosen}
            onChange={(e) => setChosen(Number(e.target.value))}
            data-testid="intake2-core-slider"
          />
          <div className="ik2-split">
            <div className="ik2-split-cell is-core" data-testid="intake2-core-cell">
              <span className="ik2-split-pct">{chosen}%</span>
              <span className="ik2-split-key">Core, following the system</span>
              <span className="ik2-split-sub">{money(core$)} of {money(mid)}</span>
            </div>
            <div className="ik2-split-cell" data-testid="intake2-rest-cell">
              <span className="ik2-split-pct">{rest}%</span>
              <span className="ik2-split-key">Explore, {placement.remainder_label}</span>
              <span className="ik2-split-sub">{money(rest$)} of {money(mid)}</span>
            </div>
          </div>
          <p className="ik-quiet">
            Those dollar figures use the midpoint of the range you picked, purely as an
            illustration. All of this money stays in your own account either way.
          </p>
        </div>

        {chosen < placement.suggested_core_pct && (
          <div className="ik-reason" data-testid="intake2-below-suggested">
            You have set Core below what we would suggest. That is a legitimate choice if you
            are running the rest yourself on purpose. It is worth a second look if the reason
            is that the drawdown numbers frightened you, because the share you hold back does
            not escape risk, it simply stops following these rules.
          </div>
        )}
        {chosen === placement.ceiling_pct && placement.ceiling_pct > placement.suggested_core_pct && (
          <div className="ik-reason" data-testid="intake2-at-ceiling">
            You have set Core at your ceiling. That is the most we would suggest for this kind
            of system, and it only works if you hold through the stretches you just read about.
          </div>
        )}
      </div>

      <div className="ik-card" data-testid="intake2-reasons">
        <div className="ik-step-label">Why those numbers</div>
        <h2>Every input that moved the dial.</h2>
        {placement.reasons.map((r, i) => (
          <div className="ik-reason" key={i} data-testid={`intake2-reason-${i}`}>{r}</div>
        ))}
      </div>

      <div className="ik-card" data-testid="intake2-skill">
        <div className="ik-step-label">Your own track record</div>
        <h2>{placement.skill.label}</h2>
        <p>{placement.skill.body}</p>
        {placement.discount_note && (
          <div className="ik2-gap" data-testid="intake2-discount-note">
            <div className="ik2-gap-head">Why we gave your own numbers less weight</div>
            <p>{placement.discount_note}</p>
          </div>
        )}
        {placement.claim_gap && (
          <div className="ik2-gap" data-testid="intake2-claim-gap">
            <div className="ik2-gap-head">One thing to call to your attention</div>
            <p>{placement.claim_gap}</p>
          </div>
        )}
      </div>

      <div className="ik-card" data-testid="intake2-remainder">
        <div className="ik-step-label">The rest of the money</div>
        <h2>Every dollar here is yours. Core is simply the part that follows the rules.</h2>
        <p>{placement.remainder_body}</p>
        <p>
          One thing to be clear about: Explore is not a safety cushion. Holding back from Core
          means a smaller part of your money follows these rules, and nothing more than that.
          Whatever you choose to do with it can fall further and faster than Core does.
        </p>
        <p className="ik-quiet">
          If you want to see one way to use that share, the QQQ Top 10 calculator at{' '}
          <a className="ik2-link" href="/top10">/top10</a> walks through a concentrated basket,
          including the two years its edge has gone flat. Nothing on this page is a
          recommendation to use it.
        </p>

        {saved ? (
          <div className="ik2-saved" data-testid="intake2-saved">
            Saved. We will use your answers to decide what to tell you, and when, during the
            stretches that actually test people.
          </div>
        ) : (
          <div className="ik-actions">
            <button type="button" className="ik-btn ik-btn-primary" onClick={onSave}
              disabled={saving} data-testid="intake2-save">
              {saving ? 'Saving…' : 'Save my profile'}
            </button>
          </div>
        )}
        <div className="ik-actions">
          <button type="button" className="ik-btn ik-btn-ghost" onClick={onRestart}
            data-testid="intake2-restart">Retake</button>
        </div>
      </div>
    </>
  );
};

const Intake2 = () => {
  const [config, setConfig] = useState(null);
  const [configError, setConfigError] = useState('');
  const [passcode, setPasscode] = useState(() => sessionStorage.getItem(PASS_KEY) || '');
  const [step, setStep] = useState(0);
  const [band, setBand] = useState(null);
  const [q1, setQ1] = useState(null);
  const [q2, setQ2] = useState(null);
  const [a1, setA1] = useState(5);
  const [a2, setA2] = useState(null);
  const [b1, setB1] = useState(null);
  const [b3, setB3] = useState(null);
  const [b4, setB4] = useState(null);
  const [b5, setB5] = useState(null);
  const [b6, setB6] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', signed: false, signature: '' });
  const [placement, setPlacement] = useState(null);
  const [chosen, setChosen] = useState(0);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    fetch(`${API_URL}/api/intake2/config`)
      .then((r) => r.json())
      .then(setConfig)
      .catch(() => setConfigError('Could not load the diagnostic. Please refresh.'));
  }, []);

  const traded = b1 !== 0;

  const payload = () => ({
    passcode,
    name: form.name.trim(),
    email: form.email.trim(),
    band,
    a1, a2, b1, q1, q2,
    // An unasked question is sent as null, never as a bad answer.
    b3: traded ? b3 : null,
    b4: traded ? b4 : null,
    b5: traded ? b5 : null,
    b6: traded ? b6 : null,
    precommit_signed: form.signed,
    precommit_name: form.signed ? form.signature.trim() : null,
  });

  // Order (Jon, 2026-10-11): who you are first, money and scenarios second, the agreement
  // last. Someone who has never traded skips the three questions about their own returns.
  const flow = ['intro', 'exp', ...(b1 === 0 ? [] : ['typical', 'repeat']),
    'values', 'amount', 'journey', 'mag', 'grind', 'agree'];
  const total = flow.length;
  const key = flow[Math.min(step, total - 1)];
  const lastStep = step >= total - 1;
  // Question numbers shift when the trading block is skipped, so they are derived, never
  // hard-coded into the copy.
  const qTime = traded ? 6 : 2;
  const qAttention = qTime + 1;
  const qAmount = qTime + 2;
  const qMag = qTime + 3;
  const qGrind = qTime + 4;

  const canAdvance = () => {
    if (key === 'intro' || key === 'journey') return true;
    if (key === 'exp') return b1 !== null;
    if (key === 'typical') return b3 !== null;
    if (key === 'repeat') return b4 !== null && b5 !== null && b6 !== null;
    if (key === 'values') return !!a2;
    if (key === 'amount') return !!band;
    if (key === 'mag') return !!q1;
    if (key === 'grind') return !!q2;
    if (key === 'agree') {
      const base = form.name.trim().length >= 2 && /\S+@\S+\.\S+/.test(form.email);
      return base && (!form.signed || form.signature.trim().length >= 2);
    }
    return false;
  };

  const score = async () => {
    setBusy(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/intake2/preview`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload()),
      });
      const body = await res.json();
      if (!res.ok) throw new Error(fmtErr(body.detail));
      setPlacement(body.placement);
      setChosen(body.placement.suggested_core_pct);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const save = async () => {
    setBusy(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/intake2/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...payload(), chosen_core_pct: chosen }),
      });
      const body = await res.json();
      if (!res.ok) throw new Error(fmtErr(body.detail));
      setSaved(true);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const restart = () => {
    setPlacement(null);
    setSaved(false);
    setStep(0);
    setBand(null);
    setQ1(null); setQ2(null);
    setA1(5); setA2(null);
    setB1(null); setB3(null); setB4(null); setB5(null); setB6(null);
    setForm({ name: '', email: '', signed: false, signature: '' });
  };

  if (!passcode) return <Gate onMember={setPasscode} />;

  if (configError) {
    return (
      <div className="ik-page"><div className="ik-shell">
        <div className="ik-error">{configError}</div>
      </div></div>
    );
  }

  if (!config) {
    return (
      <div className="ik-page" data-testid="intake2-loading">
        <div className="ik-shell"><div className="ik-empty">Loading…</div></div>
      </div>
    );
  }

  return (
    <div className="ik-page" data-testid="intake2-page">
      <div className="ik-shell">
        <Masthead
          subtitle={placement
            ? 'Here is the ceiling, and here is the part that is yours to choose.'
            : 'About you first, then the hard numbers, then an agreement with yourself.'}
        />

        {placement ? (
          <>
            {error && <div className="ik-error" data-testid="intake2-save-error">{error}</div>}
            <Result
              config={config}
              band={band}
              placement={placement}
              chosen={chosen}
              setChosen={setChosen}
              onSave={save}
              saved={saved}
              saving={busy}
              onRestart={restart}
            />
          </>
        ) : (
          <>
            <Progress step={step} total={total} />

            {key === 'intro' && <ScreenIntro />}
            {key === 'exp' && (
              <ScreenExperience config={config} b1={b1} setB1={setB1}
                n={step + 1} total={total} />
            )}
            {key === 'typical' && (
              <ScreenTypical config={config} b3={b3} setB3={setB3}
                n={step + 1} total={total} />
            )}
            {key === 'repeat' && (
              <ScreenRepeat config={config} b4={b4} b5={b5} b6={b6}
                setB4={setB4} setB5={setB5} setB6={setB6} n={step + 1} total={total} />
            )}
            {key === 'values' && (
              <ScreenValues config={config} a1={a1} a2={a2} setA1={setA1} setA2={setA2}
                n={step + 1} total={total} qTime={qTime} qAttention={qAttention} />
            )}
            {key === 'amount' && (
              <ScreenAmount config={config} band={band} setBand={setBand}
                n={step + 1} total={total} q={qAmount} />
            )}
            {key === 'journey' && (
              <ScreenJourney config={config} band={band} n={step + 1} total={total} />
            )}
            {key === 'mag' && (
              <ScreenMagnitude config={config} band={band} q1={q1} setQ1={setQ1}
                n={step + 1} total={total} q={qMag} />
            )}
            {key === 'grind' && (
              <ScreenGrind config={config} q2={q2} setQ2={setQ2}
                n={step + 1} total={total} q={qGrind} />
            )}
            {key === 'agree' && (
              <ScreenAgreement config={config} band={band} form={form} setForm={setForm}
                n={step + 1} total={total} />
            )}

            {error && <div className="ik-error" data-testid="intake2-submit-error">{error}</div>}

            <div className="ik-actions">
              {step > 0 && (
                <button type="button" className="ik-btn ik-btn-ghost"
                  onClick={() => setStep(step - 1)} data-testid="intake2-back">Back</button>
              )}
              <button
                type="button"
                className="ik-btn ik-btn-primary"
                disabled={!canAdvance() || busy}
                onClick={() => (lastStep ? score() : setStep(step + 1))}
                data-testid="intake2-next"
              >
                {busy ? 'Working…' : step === 0 ? 'Begin' : lastStep ? 'See my profile' : 'Continue'}
              </button>
            </div>

            <div className="ik-footnote">
              Nothing here is investment advice, and none of it is a promise of future
              returns. Past results, including every figure on this page, describe what has
              already happened, not what will.
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Intake2;
